import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import useService from "../../../../hooks/useService";
import { getSettlementHeaderFields } from "../helper/header";
import { useSettlementStore } from "../store/settlementStore";

export default function useSettlementPrintData() {
  const { id } = useParams();
  const providerSettlementRepository = useService("repositories/provider-settlement-repository");
  const currentUserService = useService("current-user-service");
  const caseDetail = useSettlementStore((state) => state.caseDetail);
  const feeSchedule = useSettlementStore((state) => state.feeSchedule) ?? {};
  const refreshVersion = useSettlementStore((state) => state.refreshVersion);
  const isFeeScheduleLoading = useSettlementStore((state) => state.isFeeScheduleLoading);
  const [medicalBreakdownRecords, setMedicalBreakdownRecords] = useState([]);
  const [isMedicalBreakdownLoading, setIsMedicalBreakdownLoading] = useState(false);
  const [medicalSummary, setMedicalSummary] = useState({
    totalMedicalBills: 0,
    totalIncludedInReduction: 0,
    totalSettledAmount: 0,
    totalMedicalShare: 0,
    remainingUnclaimed: 0,
  });

  useEffect(() => {
    let active = true;

    async function fetchAllMedicalBreakdown() {
      if (!id || id === "undefined") {
        if (active) {
          setMedicalBreakdownRecords([]);
          setMedicalSummary({
            totalMedicalBills: 0,
            totalIncludedInReduction: 0,
            totalSettledAmount: 0,
            totalMedicalShare: 0,
            remainingUnclaimed: 0,
          });
        }
        return;
      }

      try {
        if (active) {
          setIsMedicalBreakdownLoading(true);
        }

        const allRecords = [];
        const firstPage = await providerSettlementRepository.query({ case: id, limit: 100, offset: 0 });
        const firstRawData = await providerSettlementRepository.getRawData();

        allRecords.push(...(firstPage || []));

        if (active) {
          setMedicalSummary({
            totalMedicalBills: firstRawData?.total_medical_bills || 0,
            totalIncludedInReduction: firstRawData?.total_included_in_reduction || 0,
            totalSettledAmount: firstRawData?.total_settled_amount || 0,
            totalMedicalShare: firstRawData?.total_medical_share || 0,
            remainingUnclaimed: firstRawData?.remaining_unclaimed || 0,
          });
        }

        let nextPageUrl = await providerSettlementRepository.getNextPage();

        while (nextPageUrl) {
          const url = new URL(nextPageUrl);
          const limit = Number(url.searchParams.get("limit")) || 100;
          const offset = Number(url.searchParams.get("offset")) || allRecords.length;
          const nextPage = await providerSettlementRepository.query({ case: id, limit, offset });

          allRecords.push(...(nextPage || []));
          nextPageUrl = await providerSettlementRepository.getNextPage();
        }

        if (active) {
          setMedicalBreakdownRecords(allRecords);
        }
      } finally {
        if (active) {
          setIsMedicalBreakdownLoading(false);
        }
      }
    }

    fetchAllMedicalBreakdown();

    return () => {
      active = false;
    };
  }, [id, providerSettlementRepository, refreshVersion]);

  const headerFields = useMemo(() => getSettlementHeaderFields(caseDetail), [caseDetail]);
  const isProvider =
    currentUserService?.user?.entity?.entity_type_label === "Provider";
  const rawSettlementAmount =
    feeSchedule?.settlement_amount ??
    caseDetail?.settlementAmount ??
    caseDetail?.settlement_amount ??
    caseDetail?.actualTotalSettlementAmount ??
    caseDetail?.actual_total_settlement_amount;
  const isFeeScheduleUnavailable = rawSettlementAmount === null || rawSettlementAmount === undefined;
  const hasSettlementStatementSection = Boolean(caseDetail);
  const hasMedicalBreakdownSection = medicalBreakdownRecords.length > 0;
  const hasTotalMedicalReductionSection = !isFeeScheduleUnavailable;
  const hasFeeScheduleSection = !isFeeScheduleUnavailable;
  const hasExportableContent =
    hasMedicalBreakdownSection || hasFeeScheduleSection;

  return {
    caseDetail,
    feeSchedule,
    isDocumentLoading: isMedicalBreakdownLoading || isFeeScheduleLoading,
    isFeeScheduleLoading,
    isMedicalBreakdownLoading,
    medicalBreakdownRecords,
    medicalSummary,
    isFeeScheduleUnavailable,
    hasExportableContent,
    hasSettlementStatementSection,
    hasMedicalBreakdownSection,
    hasTotalMedicalReductionSection,
    hasFeeScheduleSection,
    headerFields,
    isProvider,
  };
}
